import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { TimeContext } from '../TimeContext';
import { resetConsent, getConsentState } from '../analytics/consent';
import './Homepage.css';
import { SiteHeader, SiteFooter } from './Homepage';

/**
 * Privacy — San Tomas Tourism College
 * ---------------------------------------------------------------
 * The college's privacy notice.
 *
 * Sections are numbered because the text cross-references them
 * ("section 2", "section 3(b)"). If you insert or remove a section,
 * check every cross-reference still points at the right place.
 *
 * Anything still in [SQUARE BRACKETS] renders in amber italics and is
 * a fact only the college can supply. Fill it in before publishing.
 *
 * Have a Kenyan data protection lawyer read this before it goes live.
 * See docs/PRIVACY-NOTICE-REVIEW.md for the open questions.
 */

/* Update this when the notice is next materially revised. Bump
   POLICY_VERSION in src/analytics/consent.js on the same day, so every
   visitor is asked for consent again against the new wording. */
const EFFECTIVE_DATE = '25 July 2026';

const SECTIONS = [
  {
    n: 1,
    heading: 'Introduction',
    body: [
      'San Tomas Tourism College ("we", "us", "our") respects your right to privacy and is committed to protecting the personal data we collect, use, and store. This Privacy Notice explains how we handle your personal information in accordance with the Data Protection Act, 2019 (Kenya) and the Data Protection (General) Regulations, 2021.',
      'We are the data controller for personal data collected through our website, santomastourismcollege.com, and through our admissions process.',
    ],
  },
  {
    n: 2,
    heading: 'Data controller and contact details',
    body: [
      'Data controller: San Tomas Tourism College',
      'Postal address: Information House, 2nd Floor, Nairobi CBD',
      'Email: info@santomastourismcollege.com',
      'Phone: +254 722 821836',
      'For any question about this notice, to exercise any of the rights described in section 11, or to raise a data protection matter, please use the contact details above.',
    ],
  },
  {
    n: 3,
    heading: 'Data we collect',
    body: ['We collect different categories of personal data depending on how you interact with us.'],
    subs: [
      {
        label: '(a) Information you provide when you apply',
        body: ['When you complete an enrolment application, we collect the information you enter on that form, which may include:'],
        list: [
          'Personal identification: full name, date of birth, nationality, national ID or passport number.',
          'Contact information: phone number, email address, physical address.',
          'Academic and professional: education history, language skills, work experience, KCSE results.',
          'Supporting documents: copies of your national ID or passport, and your KCSE certificate.',
          'Declarations: your electronic signature, your declaration that the information is accurate, your consent to the processing of your data, and whether you have opted in to receive updates from us.',
        ],
        after: ['We collect this information to assess your application for admission. Without it, we cannot process your application.'],
      },
      {
        label: '(b) Information we collect when you browse our website',
        body: ['If you accept cookies on your first visit, we use the Meta Pixel and Google Analytics to understand which of our adverts bring prospective students to the site and which pages they find useful. These tools may receive:'],
        list: [
          'The pages you visit.',
          'The courses you view, and the fees shown on those courses.',
          'Any search terms you type into the course catalogue.',
          'Whether you sent an enquiry or completed an application.',
        ],
        after: [
          'We have deliberately configured these tools NOT to receive your name, email address, phone number, national ID, date of birth, or anything you type into the application form. Form field data is not shared with Meta or Google.',
          'If you decline cookies, none of these tools load at all \u2014 not in reduced form, not anonymously. Nothing is sent.',
        ],
      },
    ],
  },
  {
    n: 4,
    heading: 'How we use your data',
    body: ['We use your personal data for the following purposes:'],
    list: [
      'Admissions: to process your application and communicate with you about its status.',
      'Academic administration: to register you as a student, administer your courses, and provide student services.',
      'Communication: to respond to your enquiries and send you important updates or notices.',
      'Marketing: if you opted in to receive updates when you applied, to send you information about our courses, intakes and events. You can ask us to stop at any time, and doing so does not affect your application.',
      'Legal compliance: to comply with legal and regulatory obligations, including reporting to relevant authorities.',
      'Website improvement: to analyse how visitors use our website \u2014 only with your consent \u2014 and improve the experience.',
    ],
    after: ['We do not use your personal data for automated decision-making or profiling.'],
  },
  {
    n: 5,
    heading: 'Lawful basis for processing',
    body: ['Under the Data Protection Act, 2019, we must have a lawful basis for processing your personal data. Our lawful bases are:'],
    list: [
      'Consent: where you have given us explicit permission \u2014 for example, for optional cookies, or to receive marketing updates.',
      'Contract: to process your application with a view to entering into a contractual relationship with you.',
      'Legal obligation: where processing is required by law, including reporting to education and student finance regulators.',
      'Legitimate interests: for our legitimate educational and administrative interests, provided these do not override your rights.',
    ],
  },
  {
    n: 6,
    heading: 'Sharing your data',
    body: ['We treat your personal data with strict confidentiality.'],
    subs: [
      {
        label: '(a) Application data',
        body: ['Your application data is sent to our internal admissions system. It is not shared with Meta, Google, or any advertising partner. It is not used to build advertising audiences.'],
      },
      {
        label: '(b) Website usage data',
        body: ['Website usage data, as described in section 3(b), is shared with:'],
        list: [
          'Meta Platforms Ireland Limited \u2014 to measure the effectiveness of our advertising.',
          'Google LLC \u2014 to analyse website traffic and usage.',
        ],
        after: ['Both may process this data outside Kenya. Section 7 explains the basis for those transfers.'],
      },
      {
        label: '(c) Other disclosures',
        body: ['We may share your data with:'],
        list: [
          'Service providers who help us operate our website, manage admissions, or deliver services \u2014 including our website host, email provider, SMS gateway, and cloud storage providers.',
          'Government regulators where required by law, including KNEC, HELB, KUCCPS and.',
          'Law enforcement, to comply with legal obligations.',
        ],
        after: ['We do not sell or rent personal data to third parties.'],
      },
    ],
  },
  {
    n: 7,
    heading: 'Transfers outside Kenya',
    body: [
      'Some of the service providers described in section 6 process data outside Kenya. Under sections 48 and 49 of the Data Protection Act, 2019, we may only transfer personal data out of the country where appropriate safeguards are in place, or where a specific statutory condition applies.',
    ],
  },
  {
    n: 8,
    heading: 'Data retention',
    body: [
      'We retain your personal data only for as long as necessary to fulfil the purposes for which it was collected, or as required by law, in line with the storage limitation principle of the Data Protection Act, 2019.',
    ],
    list: [
      'Successful applicants: your data is retained for the duration of your studies. After graduation or withdrawal, academic records are retained for five years to support transcript requests, employer verification, and alumni relations. After that, records are securely deleted or anonymised, except where a longer period is required by law \u2014 for example HELB loan records or tax obligations.',
      'Unsuccessful applicants: application data is retained for one year after the admission decision, to allow for appeals, re-evaluation, or re-application for the next intake. After that, all application records are securely deleted.',
      'Website usage data: retained by Meta and Google according to their own retention periods, which we set to the shortest practical option.',
    ],
  },
  {
    n: 9,
    heading: 'Data security',
    body: [
      'We implement appropriate technical and organisational measures to protect your personal data against loss, theft, unauthorised access, disclosure, alteration, or destruction. These include secure servers, access controls, staff training, and encryption where appropriate.',
      'No method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    n: 10,
    heading: 'Cookies and similar technologies',
    body: ['Our website uses cookies and similar technologies, including browser storage.'],
    list: [
      'Necessary: your language preference is stored in a cookie so the site opens in the language you chose. Your cookie choice itself is stored in your browser\u2019s local storage rather than in a cookie. Neither identifies you personally, and neither can be switched off without breaking the site.',
      'Analytical and advertising: the Meta Pixel and Google Analytics, described in section 3(b). These load only if you give consent through the banner shown on your first visit.',
    ],
    after: [
      'You can withdraw consent at any time using the Cookie settings link at the foot of any page, or the button at the end of this notice. Withdrawing consent is as easy as giving it, and it does not affect your application.',
      'We will ask you again if this notice changes materially, or after six months, whichever comes first.',
    ],
  },
  {
    n: 11,
    heading: 'Your rights',
    body: ['Under the Data Protection Act, 2019, you have the following rights regarding your personal data:'],
    list: [
      'To be informed: to know what personal data we collect about you, why, and how it is used.',
      'To access: to request a copy of the personal data we hold about you.',
      'To rectification: to ask us to correct inaccurate or incomplete data.',
      'To erasure: to ask us to delete your data in certain circumstances, for example where it is no longer necessary.',
      'To object: to object to how we use your data in specific circumstances.',
      'To withdraw consent: to withdraw consent at any time, where processing is based on consent.',
    ],
    after: [
      'To exercise any of these rights, contact us using the details in section 2. We will respond within the period required by the Act. There is no fee, unless a request is manifestly unfounded or excessive.',
    ],
  },
  {
    n: 12,
    heading: 'Applicants under 18',
    body: [
      'Some of our applicants are under 18. We do not use any information from applicants to build advertising audiences, and we do not target advertising on the basis of age.',
      'Where an applicant is under 18, we require the express consent of a parent or legal guardian before processing their application. The parent or guardian completes the application on behalf of the applicant and is responsible for communications with the College about the applicant\u2019s data.',
    ],
  },
  {
    n: 13,
    heading: 'Changes to this notice',
    body: [
      'We may update this notice from time to time to reflect changes in our practices, technology, or applicable law. We will post the updated notice on our website and update the effective date at the top.',
      'Where a change materially affects how we use your data, we will ask for your cookie consent again rather than relying on a choice you made against older wording.',
    ],
  },
  {
    n: 14,
    heading: 'Complaints',
    body: [
      'If you are not satisfied with how we have handled your personal data, or you believe we have breached your rights under the Data Protection Act, 2019, you may lodge a complaint with the Office of the Data Protection Commissioner, the supervisory authority for data protection in Kenya.',
      'Office of the Data Protection Commissioner, P.O. Box 30915-00100, Nairobi, Kenya. Website: www.odpc.go.ke. Email: info@odpc.go.ke',
      'We would appreciate the opportunity to address your concerns first. Please contact us using the details in section 2, and we will do our utmost to resolve the matter.',
    ],
  },
];

/* ── presentation helpers ─────────────────────────────────────── */

const isPlaceholder = (s) => s.trim().startsWith('[');

const para = (text, i) => (
  <p
    key={i}
    style={{
      fontFamily: "'Poppins',sans-serif",
      fontSize: 14,
      lineHeight: 1.85,
      color: isPlaceholder(text) ? '#B45309' : '#4B5563',
      fontStyle: isPlaceholder(text) ? 'italic' : 'normal',
      margin: '0 0 12px',
    }}
  >
    {text}
  </p>
);

const bullets = (items) => (
  <ul style={{ margin: '0 0 14px', paddingLeft: 20 }}>
    {items.map((li, i) => (
      <li
        key={i}
        style={{
          fontFamily: "'Poppins',sans-serif",
          fontSize: 14,
          lineHeight: 1.85,
          color: isPlaceholder(li) ? '#B45309' : '#4B5563',
          marginBottom: 8,
        }}
      >
        {li}
      </li>
    ))}
  </ul>
);

const Privacy = () => {
  const { t, i18n } = useTranslation();
  const currentTime = useContext(TimeContext);
  const [state, setState] = useState(getConsentState());

  const navLinks = [
    { to: '/', label: t('nav.home', 'Home'), ariaLabel: t('nav.home', 'Home') },
    { to: '/programs', label: t('nav.programs', 'Programs'), ariaLabel: t('nav.programs', 'Programs') },
    { to: '/campus-life', label: t('nav.campusLife', 'Campus Life'), ariaLabel: t('nav.campusLife', 'Campus Life') },
    { to: '/alumni', label: t('nav.alumni', 'Alumni'), ariaLabel: t('nav.alumni', 'Alumni') },
    { to: '/enrollment', label: t('nav.enrollment', 'Enrollment'), ariaLabel: t('nav.enrollment', 'Enrollment') },
  ];

  const withdraw = () => {
    resetConsent();
    setState(null);
  };

  const cardStyle = {
    background: '#fff',
    border: '1px solid rgba(65,105,225,0.12)',
    borderRadius: 12,
    padding: '28px 32px',
    marginBottom: 18,
    boxShadow: '0 4px 24px rgba(65,105,225,0.06)',
  };

  return (
    <div className="min-h-screen" style={{ background: '#EEF1FC' }}>
      <Helmet>
        <title>Privacy notice — San Tomas Tourism College</title>
        <meta
          name="description"
          content="How San Tomas Tourism College collects, uses and protects personal data, under the Data Protection Act, 2019."
        />
        <link rel="canonical" href="https://santomastourismcollege.com/privacy" />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <SiteHeader t={t} i18n={i18n} navLinks={navLinks} />

      <section style={{ background: 'linear-gradient(135deg, #1E3A8A, #4169E1)', padding: '120px 0 52px' }}>
        <div className="sttc-container">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-eyebrow" style={{ color: 'rgba(251,191,36,0.9)' }}>
              Effective {EFFECTIVE_DATE}
            </div>
            <h1 className="section-title" style={{ color: '#fff', fontSize: 'clamp(30px,4.5vw,48px)' }}>
              Privacy <em style={{ color: '#FBBF24' }}>notice</em>
            </h1>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.78)', maxWidth: 580, lineHeight: 1.8, fontWeight: 300, marginTop: 12 }}>
              What we collect, why we collect it, who else sees it, and how to tell us to stop.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="sttc-stripe" />

      <section id="main-content" style={{ padding: '48px 0 88px' }}>
        <div className="sttc-container" style={{ maxWidth: 800 }}>

          <nav aria-label="Contents" style={{ ...cardStyle, padding: '22px 32px' }}>
            <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 16, fontWeight: 900, color: '#1E3A8A', margin: '0 0 12px' }}>
              Contents
            </h2>
            <ol style={{ margin: 0, paddingLeft: 20, columns: 2, columnGap: 28 }}>
              {SECTIONS.map((s) => (
                <li
                  key={s.n}
                  style={{ fontFamily: "'Poppins',sans-serif", fontSize: 13, lineHeight: 1.9, color: '#4B5563', breakInside: 'avoid' }}
                >
                  <a href={`#section-${s.n}`} style={{ color: '#4169E1', textDecoration: 'none' }}>
                    {s.heading}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          {SECTIONS.map((s) => (
            <div key={s.n} id={`section-${s.n}`} style={{ ...cardStyle, scrollMarginTop: 100 }}>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 21, fontWeight: 900, color: '#1E3A8A', margin: '0 0 14px' }}>
                <span style={{ color: '#FBBF24', marginRight: 10 }}>{s.n}.</span>
                {s.heading}
              </h2>

              {(s.body || []).map(para)}
              {s.list && bullets(s.list)}
              {(s.after || []).map((p, i) => para(p, `a${i}`))}

              {(s.subs || []).map((sub) => (
                <div key={sub.label} style={{ marginTop: 18, paddingLeft: 16, borderLeft: '3px solid rgba(65,105,225,0.15)' }}>
                  <h3 style={{ fontFamily: "'Poppins',sans-serif", fontSize: 14.5, fontWeight: 600, color: '#1E3A8A', margin: '0 0 10px' }}>
                    {sub.label}
                  </h3>
                  {(sub.body || []).map(para)}
                  {sub.list && bullets(sub.list)}
                  {(sub.after || []).map((p, i) => para(p, `sa${i}`))}
                </div>
              ))}
            </div>
          ))}

          <div
            style={{
              background: '#1E3A8A',
              borderRadius: 12,
              padding: '28px 32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 20,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: 19, fontWeight: 900, color: '#FBBF24', margin: '0 0 4px' }}>
                Your current choice
              </h2>
              <p style={{ fontFamily: "'Poppins',sans-serif", fontSize: 13.5, color: 'rgba(255,255,255,0.82)', margin: 0, fontWeight: 300 }}>
                {state === 'granted'
                  ? 'You have accepted analytical and advertising cookies.'
                  : state === 'denied'
                  ? 'You have declined analytical and advertising cookies. Nothing is being loaded.'
                  : 'You have not chosen yet.'}
              </p>
            </div>
            <button
              type="button"
              onClick={withdraw}
              style={{
                fontFamily: "'Poppins',sans-serif",
                fontSize: 13,
                fontWeight: 600,
                padding: '11px 24px',
                borderRadius: 8,
                border: '1.5px solid #FBBF24',
                background: 'transparent',
                color: '#FBBF24',
                cursor: 'pointer',
              }}
            >
              Change my choice
            </button>
          </div>
        </div>
      </section>

      <SiteFooter t={t} currentTime={currentTime} />
    </div>
  );
};

export default Privacy;
